/**
 * ============================================================================
 * USE PAYMENT HOOK
 * ============================================================================
 * 
 * Custom hook for payment operations
 */

import { useState, useCallback } from 'react';
import * as paymentService from '../services/payments';

export const usePayment = (paymentId = null) => {
  const [payment, setPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);

  /**
   * Initiate payment
   */
  const initiatePayment = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.initiatePayment(data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to initiate payment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get payment by ID
   */
  const getPayment = useCallback(async (id = paymentId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPayment(id);
      setPayment(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get payment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  /**
   * Verify payment
   */
  const verifyPayment = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.verifyPayment(id, data);
      if (id === paymentId) {
        setPayment(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Payment verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  /**
   * Get payment status
   */
  const getPaymentStatus = useCallback(async (id = paymentId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentStatus(id);
      if (id === paymentId) {
        setPayment(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get payment status');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  /**
   * Cancel payment
   */
  const cancelPayment = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.cancelPayment(id);
      if (id === paymentId) {
        setPayment(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to cancel payment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  /**
   * Refund payment
   */
  const refundPayment = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.refundPayment(id, data);
      if (id === paymentId) {
        setPayment(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to refund payment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  /**
   * List payments
   */
  const listPayments = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.listPayments(filters);
      setPayments(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to list payments');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get payment stats
   */
  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentStats();
      setStats(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load payment stats');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get payment methods
   */
  const loadPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentMethods();
      setPaymentMethods(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load payment methods');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get payment history
   */
  const getPaymentHistory = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentHistory(filters);
      setPayments(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get payment history');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Retry payment
   */
  const retryPayment = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.retryPayment(id);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to retry payment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    payment,
    payments,
    stats,
    paymentMethods,
    loading,
    error,
    
    // Actions
    initiatePayment,
    getPayment,
    verifyPayment,
    getPaymentStatus,
    cancelPayment,
    refundPayment,
    listPayments,
    loadStats,
    loadPaymentMethods,
    getPaymentHistory,
    retryPayment,
    
    // Convenience
    refresh: paymentId ? () => getPayment(paymentId) : null,
  };
};

export default usePayment;
